import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Row, Col } from "react-bootstrap";
import Button from "react-bootstrap/Button";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import ReportList from "../../../common/ReportList";
import Constants from "../../../../common/Constants";
import { formatDateTime, getCurrentDate } from "../../../../common/Validations";
import { convertSecondsToHours } from "../../../../components/common/util";
import "./OperatorsEfficiency.scss";

const formatEfficiency = (value) =>
  value === null || value === undefined ? "N/A" : `${value}%`;

const formatCycleTime = (value) => {
  if (value === null || value === undefined) return "Not configured";
  return `${Number(value).toLocaleString()} min`;
};

const getPerformance = (value) => {
  if (value === null || value === undefined) {
    return { label: "Cycle time missing", className: "missing" };
  }
  if (value >= 100) return { label: "Above standard", className: "good" };
  if (value >= 80) return { label: "On track", className: "steady" };
  return { label: "Needs review", className: "review" };
};

const formatVariance = (value) => {
  if (value === null || value === undefined) return "N/A";
  const label = value >= 0 ? "saved" : "over";
  return `${convertSecondsToHours(Math.abs(value))} ${label}`;
};

const OperatorsEfficiency = () => {
  const { operatorUserId, dateFrom, dateTo } = useSelector(
    (state) => state.operatorReport
  );
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [displayFilter, setDisplayFilter] = useState(false);

  const columnsData = [
    {
      name: "Date",
      selector: (row) => row.operatorcreatedDate,
      sortable: true,
      width: "155px",
      cell: (row) => <p className="m-0 p-0">{formatDateTime(row.operatorcreatedDate)}</p>,
    },
    {
      name: "Operator",
      selector: (row) => row.operatorUserId,
      sortable: true,
      width: "145px",
      cell: (row) => (
        <div>
          <p className="m-0 p-0 fw-bold">{row.operatorUserId}</p>
          <p className="m-0 p-0">{row.operatorUserName}</p>
        </div>
      ),
    },
    {
      name: "Jobcard",
      selector: (row) => row.jobcard,
      sortable: true,
      width: "145px",
      cell: (row) => (
        <span className="bg-table-status bg-table-new-status">{row.jobcard}</span>
      ),
    },
    {
      name: "Size",
      selector: (row) => row.sizeCode,
      sortable: true,
      width: "190px",
      cell: (row) => (
        <div>
          <p className="m-0 p-0 fw-bold">{row.sizeCode || "-"}</p>
          <p className="m-0 p-0 operator-muted-text">{row.sizeName || ""}</p>
        </div>
      ),
    },
    {
      name: "Department",
      selector: (row) => row.toDeptCode,
      sortable: true,
      width: "175px",
      cell: (row) => (
        <div>
          <p className="m-0 p-0 fw-bold">{row.toDeptCode}</p>
          <p className="m-0 p-0">{row.toDeptName}</p>
        </div>
      ),
    },
    {
      name: "Output Qty",
      selector: (row) => row.okQuantity + row.rejectQuantity,
      sortable: true,
      width: "130px",
      cell: (row) => (
        <div className="operator-output-qty">
          <span className="operator-output-qty__ok">OK {row.okQuantity}</span>
          <span className="operator-output-qty__reject">Reject {row.rejectQuantity}</span>
        </div>
      ),
    },
    {
      name: "Cycle Time",
      selector: (row) => row.cycleTimeMinutes,
      sortable: true,
      width: "130px",
      cell: (row) => (
        <span
          className={`operator-cycle-time ${
            row.targetDurationSource === "MISSING_CYCLE_TIME"
              ? "operator-cycle-time--missing"
              : ""
          }`}
          title="Department cycle time from Process Master"
        >
          {formatCycleTime(row.cycleTimeMinutes)}
        </span>
      ),
    },
    {
      name: "Time Comparison",
      selector: (row) => row.targetDuration,
      sortable: true,
      width: "190px",
      cell: (row) => (
        <div className="operator-time-comparison">
          <span>
            Standard
            <strong>
              {row.targetDuration ? convertSecondsToHours(row.targetDuration) : "N/A"}
            </strong>
          </span>
          <span>
            Actual <strong>{convertSecondsToHours(row.actualDuration)}</strong>
          </span>
        </div>
      ),
    },
    {
      name: "Variance",
      selector: (row) => row.durationVariance,
      sortable: true,
      width: "145px",
      cell: (row) => (
        <span
          className={
            row.durationVariance === null || row.durationVariance === undefined
              ? "operator-variance--missing"
              : row.durationVariance >= 0
                ? "operator-variance--saved"
                : "operator-variance--over"
          }
        >
          {formatVariance(row.durationVariance)}
        </span>
      ),
    },
    {
      name: "Efficiency",
      selector: (row) => row.efficiencyPercentage,
      sortable: true,
      width: "165px",
      cell: (row) => {
        const performance = getPerformance(row.efficiencyPercentage);
        return (
          <div
            className={`operator-performance operator-performance--${performance.className}`}
            title="Standard duration / actual duration × 100"
          >
            <strong>{formatEfficiency(row.efficiencyPercentage)}</strong>
            <span>{performance.label}</span>
          </div>
        );
      },
    },
    {
      name: "Machine",
      selector: (row) => row.toMachineCode,
      sortable: true,
      width: "130px",
      cell: (row) => <p className="m-0 p-0">{row.toMachineCode || "-"}</p>,
    },
  ];

  return (
    <main className="generatejobcard main" id="main">
      <Row className="justify-center subContent">
        <Col xs={1} md={1}>
          <Button
            type="button"
            variant="primary"
            className="btn btn-primary btn-xs navigateBackButton"
            onClick={() => navigate("/OperatorsReports")}
          >
            <i className="bi bi-arrow-left back"> </i>
            <span className="back"> {t("msg_common.Back")}</span>
          </Button>
        </Col>

        <Col xs={11} md={11} className="rightSide">
          <div className="rightSideInner timesheet">
            <h2 className="mainTitle">
              Operators Efficiency -{" "}
              {dateFrom ? (
                <>
                  <span className="bg-table-status bg-table-documentNumber-status headerStatusDetailedPage">
                    {dateFrom}
                  </span>
                  {" to "}
                  <span className="bg-table-status bg-table-documentNumber-status headerStatusDetailedPage">
                    {dateTo}
                  </span>
                </>
              ) : (
                <span className="bg-table-status bg-table-documentNumber-status headerStatusDetailedPage">
                  {getCurrentDate()}
                </span>
              )}
            </h2>
            <p className="subText">Operator efficiency by jobcard and department</p>
            <div className="operator-efficiency-explainer">
              <div>
                <span className="operator-efficiency-explainer__label">Calculation</span>
                <strong>Efficiency = Standard Duration ÷ Actual Duration × 100</strong>
              </div>
              <p>
                Standard Duration comes from the selected size's department cycle time in
                Process Master. Missing cycle times show N/A instead of a misleading result.
              </p>
            </div>
          </div>
        </Col>

        <hr />
      </Row>

      <ReportList
        columnsData={columnsData}
        displayFilter={displayFilter}
        resetFilerBox={setDisplayFilter}
        pageName={t("msg_reports.msg_operator_efficiency.operator_efficiency_title")}
        pageURL={Constants.REPORTS.OPERATOREFFICIENCY.list}
        name={Constants.REPORTS.OPERATOREFFICIENCY.name}
        parameter={{ operatorUserId, dateFrom, dateTo }}
        selectableRows={false}
      />
    </main>
  );
};

export default OperatorsEfficiency;
